/**
 * The live worst-case wait (docs/plans/oncall-line-plan.md 2.3, 7.7; WP-11):
 * how long a caller can hold before voicemail with the hunt settings as they
 * stand in the form and the line's active members, so the trade-off of a
 * longer ring or smaller waves is visible before saving. The number is
 * huntMath.worstCaseSeconds, the port of the backend's hunt.worstCaseSeconds.
 */
import { Timer } from 'lucide-react';
import { formatDurationWords } from '../../lib/format';
import { SCREEN_SECONDS, totalWaves, worstCaseSeconds } from './huntMath';

export interface WorstCaseWaitProps {
  ringSeconds: number | null;
  waveSize: number | null;
  maxPasses: number | null;
  /** Active members of the line (GET /health active_members); null while unknown. */
  activeMembers: number | null;
}

export default function WorstCaseWait({ ringSeconds, waveSize, maxPasses, activeMembers }: WorstCaseWaitProps) {
  const box = 'flex items-start gap-3 rounded-xl border border-primary-200 bg-primary-50 p-3 text-sm text-primary-900 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-100';

  let text: string;
  let detail: string | null = null;
  if (ringSeconds === null || waveSize === null || maxPasses === null) {
    text = 'Fix the ring time, phones at once and passes to see the longest wait.';
  } else if (activeMembers === null) {
    text = 'The active-member count could not be read, so the longest wait is unknown.';
  } else if (activeMembers === 0) {
    text = 'No active members: calls go straight to the fallback numbers, then voicemail.';
  } else {
    const settings = { ring_seconds: ringSeconds, wave_size: waveSize, max_passes: maxPasses };
    const seconds = worstCaseSeconds(settings, activeMembers);
    const waves = totalWaves(activeMembers, waveSize, maxPasses);
    const people = activeMembers === 1 ? '1 active member' : `${activeMembers} active members`;
    text = `Up to ${formatDurationWords(seconds)} before voicemail`;
    detail = `${people}, ${waveSize === 1 ? 'one phone' : `${waveSize} phones`} at a time: ${waves} ${
      waves === 1 ? 'wave' : 'waves'
    } of a ${ringSeconds} s ring plus up to ${SCREEN_SECONDS} s for the press-1 screen. The greeting and hold messages add a few seconds.`;
  }

  return (
    <div className={box} data-testid="oncall-worst-case" aria-live="polite">
      <Timer className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
      <div className="min-w-0">
        <p className="font-medium" data-testid="oncall-worst-case-text">
          {text}
        </p>
        {detail && <p className="mt-0.5 text-xs text-primary-800 dark:text-primary-200">{detail}</p>}
      </div>
    </div>
  );
}
